import type React from "react";
import {useRef} from "react";

import {useFrame} from "@react-three/fiber";
import {Matrix4, type Mesh, Quaternion, Vector3} from "three";

import {useSelection} from "../useSelection";
import {DraggableTransform} from "./drag/DraggableTransform";
import {useButtonScale} from "./useButtonScale";

interface RotateButtonProps {
  relativePosition: [number, number]; // Relative position: [-1, -1] to [1, 1]
}

const translation = new Vector3();
const dummyQuat = new Quaternion();
const dummyScale = new Vector3();
const shiftToCenter = new Matrix4();
const rotationMatrix = new Matrix4();
const shiftBack = new Matrix4();

const RotateButton: React.FC<RotateButtonProps> = ({relativePosition}) => {
  const {buttonScale} = useButtonScale();
  const {
    selectionBoundingBox,
    applyTransformToSelected,
    recalculateBoundingBox,
  } = useSelection();
  const buttonRef = useRef<Mesh>(null);

  // Stores the current ideal position of the button computed every frame.
  const initialButtonPosition = useRef<Vector3>(new Vector3());

  const handleRotateTransform = (deltaMatrix: Matrix4) => {
    if (!selectionBoundingBox?.current) return;

    // Get the translation from the delta. (No button position update is done here.)
    deltaMatrix.decompose(translation, dummyQuat, dummyScale);

    // The virtual handle position is as if we had applied the translation to the baseline.
    const virtualPosition = initialButtonPosition.current
      .clone()
      .add(translation);
    const center = selectionBoundingBox.current.position;

    // Calculate the initial and current angles (relative to the selection center)
    const initialAngle = Math.atan2(
      initialButtonPosition.current.y - center.y,
      initialButtonPosition.current.x - center.x,
    );
    const currentAngle = Math.atan2(
      virtualPosition.y - center.y,
      virtualPosition.x - center.x,
    );
    const rotationAngle = currentAngle - initialAngle;

    // Create a rotation around the selection center.
    shiftToCenter.makeTranslation(-center.x, -center.y, 0);
    rotationMatrix.makeRotationZ(rotationAngle);
    shiftBack.makeTranslation(center.x, center.y, 0);

    applyTransformToSelected([shiftToCenter, rotationMatrix, shiftBack], true);
  };

  const handleDragEnd = () => {
    recalculateBoundingBox();
  };

  useFrame(() => {
    if (!buttonRef.current) return;
    buttonRef.current.scale.set(0, 0, 0);

    if (!selectionBoundingBox?.current) return;

    // Calculate the button's desired position using the selection's bounding data.
    const fac = 1;
    const pos = new Vector3(
      relativePosition[0] / 2 +
        (relativePosition[0] * fac * buttonScale.current) /
          selectionBoundingBox.current.scale.x,
      relativePosition[1] / 2 +
        (relativePosition[1] * fac * buttonScale.current) /
          selectionBoundingBox.current.scale.y,
      0,
    );
    // Convert local selection coordinates to world coordinates.
    selectionBoundingBox.current.localToWorld(pos);
    pos.z = 51; // Render the button above the selection.
    buttonRef.current.position.copy(pos);

    // Update the baseline position.
    initialButtonPosition.current.copy(pos);

    // Update the scale.
    buttonRef.current.scale.set(buttonScale.current, buttonScale.current, 1);
  });

  return (
    <DraggableTransform
      onTransform={handleRotateTransform}
      onEnd={handleDragEnd}
    >
      <mesh ref={buttonRef}>
        <circleGeometry args={[0.55, 32]} />
        <meshBasicMaterial color="black" />
        <mesh>
          <circleGeometry args={[0.5, 32]} />
          <meshBasicMaterial color="green" />
        </mesh>
      </mesh>
    </DraggableTransform>
  );
};

export default RotateButton;
